/**
 * resourceCacheService.js
 * Caches AI-generated resources (audio scripts, slides, lab HTML) to:
 *   1. Firestore `generatedResources` — for instant restore on next visit
 *   2. Google Drive `Ekamanam/Resources/` — for cross-device access
 *
 * Also provides Bloom's Taxonomy level mapping per resource type.
 */

import { db } from '../firebase/config';
import {
  collection, addDoc, getDocs, query,
  where, orderBy, limit, serverTimestamp,
} from 'firebase/firestore';
import { hasDrivePermissions, saveResourceToDrive } from './googleDriveService';

// ─────────────────────────────────────────────
// BLOOM'S TAXONOMY MAPPING
// Maps resource type keys → Bloom's levels exercised
// ─────────────────────────────────────────────
export const RESOURCE_BLOOMS = {
  audio:              ['Remember', 'Understand'],
  slides:             ['Remember', 'Understand'],
  lab_quiz_game:      ['Remember', 'Understand'],
  lab_concept_sorter: ['Understand', 'Apply'],
  lab_matching_game:  ['Remember'],
  lab_timeline:       ['Understand', 'Analyze'],
  lab_process_sim:    ['Apply', 'Analyze'],
  lab_decision_tree:  ['Analyze', 'Evaluate'],
};

// ─────────────────────────────────────────────
// SAVE
// ─────────────────────────────────────────────

/**
 * Save a generated resource to Firestore and mirror to Drive (non-blocking).
 *
 * @param {string} userId
 * @param {string} pdfId     — stable identifier for the PDF (name or Drive file ID)
 * @param {string} pdfName
 * @param {'audio'|'slides'|'lab'} type
 * @param {string} key       — unique within type, e.g. 'lecture_medium', 'Professional_8'
 * @param {object|string} content  — full generated JSON object or HTML string
 * @param {object} config    — config used at generation time (includes .type for labs)
 * @returns {Promise<string>} Firestore document ID
 */
export const saveResource = async (userId, pdfId, pdfName, type, key, content, config = {}) => {
  if (!userId || !pdfId) return null;
  try {
    const bloomsKey = type === 'lab' ? `lab_${config.type}` : type;
    const bloomsLevels = RESOURCE_BLOOMS[bloomsKey] || ['Remember'];

    const docRef = await addDoc(collection(db, 'generatedResources'), {
      userId,
      pdfId,
      pdfName: pdfName || '',
      type,
      key,
      content,
      config,
      bloomsLevels,
      createdAt: serverTimestamp(),
      driveFileId: null,
    });

    // Mirror to Drive asynchronously — never blocks the UI
    if (hasDrivePermissions()) {
      saveResourceToDrive(docRef.id, pdfName, type, key, content).catch(() => {});
    }

    return docRef.id;
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────
// LOAD
// ─────────────────────────────────────────────

/**
 * Load the most recent cached resource for a (userId, pdfId, type, key) tuple.
 *
 * @returns {Promise<object|null>} — { id, content, config, bloomsLevels, createdAt, ... } or null
 */
export const loadResource = async (userId, pdfId, type, key) => {
  if (!userId || !pdfId) return null;
  try {
    const q = query(
      collection(db, 'generatedResources'),
      where('userId', '==', userId),
      where('pdfId', '==', pdfId),
      where('type', '==', type),
      where('key', '==', key),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() };
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────
// LIST
// ─────────────────────────────────────────────

/**
 * List all cached resources for a (userId, pdfId) pair, newest first.
 *
 * @returns {Promise<Array>}
 */
export const listResources = async (userId, pdfId) => {
  if (!userId || !pdfId) return [];
  try {
    const q = query(
      collection(db, 'generatedResources'),
      where('userId', '==', userId),
      where('pdfId', '==', pdfId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch {
    return [];
  }
};
